import { NEAR_RPC_URL } from "../config";
import { callContractView } from "../api/near";

const capabilityCache = new Map();

const LEGACY_CAPABILITY_OVERRIDES = new Map([
  [
    "initiate.nearlegion.near",
    {
      kind: "sbt",
      isSbt: true,
      canSend: false,
      canSell: false,
      canApproveMarket: false,
      transferMode: "soulbound",
      reasonCode: "legacy_sbt",
      confidence: "confirmed",
      source: "compatibility_registry",
    },
  ],
]);

function normalizeContractId(value) {
  return String(value || "").trim().toLowerCase();
}

async function rpcQuery(params, { signal } = {}) {
  const response = await fetch(NEAR_RPC_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: "nft-capabilities",
      method: "query",
      params,
    }),
    signal,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.error) {
    const message =
      payload?.error?.message ||
      payload?.error?.data ||
      `NEAR RPC error (${response.status})`;
    throw new Error(String(message));
  }
  return payload?.result ?? null;
}

function decodeBase64(value) {
  const encoded = String(value || "").trim();
  if (!encoded) return new Uint8Array();
  const decode = globalThis?.atob;
  if (typeof decode !== "function") {
    throw new Error("Base64 decoding is unavailable");
  }
  const binary = decode(encoded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function fetchContractCodeInfo(contractId, { signal } = {}) {
  const [accountResult, codeResult] = await Promise.all([
    rpcQuery(
      {
        request_type: "view_account",
        finality: "final",
        account_id: contractId,
      },
      { signal }
    ),
    rpcQuery(
      {
        request_type: "view_code",
        finality: "final",
        account_id: contractId,
      },
      { signal }
    ),
  ]);

  const codeHash = String(accountResult?.code_hash || "").trim();
  const codeBytes = decodeBase64(codeResult?.code_base64 || codeResult?.code || "");
  let methods = [];

  if (
    codeBytes.length > 0 &&
    typeof WebAssembly !== "undefined" &&
    typeof WebAssembly.compile === "function" &&
    typeof WebAssembly.Module?.exports === "function"
  ) {
    const module = await WebAssembly.compile(codeBytes);
    methods = WebAssembly.Module.exports(module)
      .map((entry) => String(entry?.name || "").trim())
      .filter(Boolean);
  }

  return {
    codeHash,
    methods,
  };
}

function isStrictSbtMetadata(metadata) {
  const spec = String(metadata?.spec || "").trim().toLowerCase();
  return /^sbt(?:-|$)/.test(spec);
}

export function resolveNftCapabilityFromSignals({
  contractId,
  codeHash = "",
  methods = [],
  sbtMetadata = null,
  legacyOverride = null,
  inspectionSucceeded = true,
} = {}) {
  const normalizedContractId = normalizeContractId(contractId);
  const exported = new Set((Array.isArray(methods) ? methods : []).map((value) => String(value || "").trim()));

  if (isStrictSbtMetadata(sbtMetadata)) {
    return {
      contractId: normalizedContractId,
      codeHash,
      kind: "sbt",
      isSbt: true,
      canSend: false,
      canSell: false,
      canApproveMarket: false,
      transferMode: "soulbound",
      reasonCode: "nep393_sbt",
      confidence: "confirmed",
      source: "sbt_metadata",
      verified: true,
      sbtSpec: String(sbtMetadata?.spec || "").trim(),
      methods: Array.from(exported),
    };
  }

  if (legacyOverride) {
    return {
      contractId: normalizedContractId,
      codeHash,
      ...legacyOverride,
      verified: true,
      methods: Array.from(exported),
    };
  }

  if (!inspectionSucceeded) {
    return {
      contractId: normalizedContractId,
      codeHash,
      kind: "unknown",
      isSbt: false,
      canSend: false,
      canSell: false,
      canApproveMarket: false,
      transferMode: "unknown",
      reasonCode: "capability_unverified",
      confidence: "low",
      source: "fail_closed",
      verified: false,
      methods: [],
    };
  }

  if (exported.has("sbt_metadata")) {
    return {
      contractId: normalizedContractId,
      codeHash,
      kind: "sbt_candidate",
      isSbt: true,
      canSend: false,
      canSell: false,
      canApproveMarket: false,
      transferMode: "soulbound_or_unknown",
      reasonCode: "sbt_metadata_unverified",
      confidence: "medium",
      source: "sbt_metadata_export",
      verified: false,
      methods: Array.from(exported),
    };
  }

  const hasTransfer = exported.has("nft_transfer");
  const hasTransferCall = exported.has("nft_transfer_call");
  const hasApprove = exported.has("nft_approve");

  if (!hasTransfer) {
    return {
      contractId: normalizedContractId,
      codeHash,
      kind: "non_transferable_nft",
      isSbt: false,
      canSend: false,
      canSell: false,
      canApproveMarket: false,
      transferMode: "non_transferable",
      reasonCode: "no_nft_transfer",
      confidence: "high",
      source: "wasm_exports",
      verified: true,
      methods: Array.from(exported),
    };
  }

  return {
    contractId: normalizedContractId,
    codeHash,
    kind: "nft",
    isSbt: false,
    canSend: true,
    canSell: hasApprove,
    canApproveMarket: hasApprove,
    transferMode: "standard",
    reasonCode: hasApprove ? "transferable_market_compatible" : "no_nft_approve",
    confidence: "high",
    source: "wasm_exports",
    verified: true,
    hasTransferCall,
    methods: Array.from(exported),
  };
}

export async function classifyNftContract(contractId, { force = false, signal } = {}) {
  const normalizedContractId = normalizeContractId(contractId);
  if (!normalizedContractId) {
    return resolveNftCapabilityFromSignals({
      contractId: "",
      inspectionSucceeded: false,
    });
  }

  let codeInfo;
  try {
    codeInfo = await fetchContractCodeInfo(normalizedContractId, { signal });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    const legacyOverride = LEGACY_CAPABILITY_OVERRIDES.get(normalizedContractId) || null;
    if (legacyOverride) {
      return resolveNftCapabilityFromSignals({
        contractId: normalizedContractId,
        legacyOverride,
        inspectionSucceeded: false,
      });
    }
    return resolveNftCapabilityFromSignals({
      contractId: normalizedContractId,
      inspectionSucceeded: false,
    });
  }

  const cacheKey = `${normalizedContractId}:${codeInfo.codeHash || "no-code-hash"}`;
  if (!force && capabilityCache.has(cacheKey)) {
    return capabilityCache.get(cacheKey);
  }

  const request = (async () => {
    const legacyOverride = LEGACY_CAPABILITY_OVERRIDES.get(normalizedContractId) || null;
    let sbtMetadata = null;

    if (codeInfo.methods.includes("sbt_metadata")) {
      try {
        sbtMetadata = await callContractView(normalizedContractId, "sbt_metadata", {}, { signal });
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        sbtMetadata = null;
      }
    }

    return resolveNftCapabilityFromSignals({
      contractId: normalizedContractId,
      codeHash: codeInfo.codeHash,
      methods: codeInfo.methods,
      sbtMetadata,
      legacyOverride,
      inspectionSucceeded: true,
    });
  })().catch((error) => {
    capabilityCache.delete(cacheKey);
    if (error?.name === "AbortError") throw error;
    return resolveNftCapabilityFromSignals({
      contractId: normalizedContractId,
      codeHash: codeInfo.codeHash,
      inspectionSucceeded: false,
    });
  });

  capabilityCache.set(cacheKey, request);
  return request;
}

export function clearNftCapabilityCache(contractId = "") {
  const normalizedContractId = normalizeContractId(contractId);
  if (!normalizedContractId) {
    capabilityCache.clear();
    return;
  }
  for (const key of capabilityCache.keys()) {
    if (key.startsWith(`${normalizedContractId}:`)) capabilityCache.delete(key);
  }
}

export function getNftCapabilityMessageKey(capability) {
  if (!capability || capability.reasonCode === "capability_loading") {
    return "nftCapability.checking";
  }
  if (capability.isSbt) return "nftCapability.sbtBlocked";
  if (!capability.verified) return "nftCapability.unverifiedBlocked";
  if (!capability.canSend) return "nftCapability.nonTransferableBlocked";
  if (!capability.canSell) return "nftCapability.noApprovalBlocked";
  return "";
}
